import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';

import { PermissionsService } from '../services/permissions.service';
import { ActiveTenantService } from '../services/active-tenant.service';
import { AuthService } from '../services/auth.service';
import { MeService } from '../services/me.service';
import { CapabilitiesService } from '../services/capabilities.service';

/** Gates /security: owner OR security_viewer capability. Resolves tenant_role + capabilities first on a cold deep-link. */
export const securityPostureGuard: CanActivateFn = async () => {
  const perms = inject(PermissionsService);
  const active = inject(ActiveTenantService);
  const auth = inject(AuthService);
  const me = inject(MeService);
  const caps = inject(CapabilitiesService);
  const router = inject(Router);

  // Fast path: signals already resolved (normal in-app navigation).
  // hasTenantCapability short-circuits to true for a tenant owner.
  if (perms.hasTenantCapability('security_viewer')()) {
    return true;
  }

  const claims = auth.claims();
  const tenantId = claims.activeTenantId;
  if (!tenantId) {
    return router.parseUrl('/projects'); // no active tenant context to gate on
  }

  // Resolve tenant_role: ensure the membership list (carrying tenant_role) is loaded.
  if (active.tenants().length === 0) {
    const rows = await firstValueFrom(me.listMyTenants().pipe(catchError(() => of([]))));
    active.tenants.set(rows);
  }
  if (perms.hasTenantRole('owner')()) {
    return true;
  }

  // Member: allowed only with the security_viewer capability for the active tenant.
  if (claims.userId) {
    await firstValueFrom(caps.fetchForCaller(tenantId, claims.userId));
  }
  if (perms.hasTenantCapability('security_viewer')()) {
    return true;
  }

  return router.parseUrl('/projects');
};
